import { useState } from "react";
import { ShieldCheck, FileSearch, Download, Loader2, AlertTriangle, Gauge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SicAutorizacionProspecto } from "@/components/common/SicAutorizacionProspecto";
import { api, apiError } from "@/lib/api";
import { mxn, fdate } from "@/lib/format";
import { toast } from "sonner";

const SOCIEDADES = ["Buró de Crédito", "Círculo de Crédito"];
const PRODUCTOS = ["Crédito simple", "Reporte de crédito", "Reporte especial"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function scoreColor(s) {
  if (!s) return "text-slate-400";
  if (s >= 700) return "text-emerald-600";
  if (s >= 620) return "text-amber-600";
  return "text-red-600";
}

export function SicConsulta({ cliente, autorizaciones = [], consultas = [], onChange }) {
  const [sociedad, setSociedad] = useState("Buró de Crédito");
  const [producto, setProducto] = useState("Crédito simple");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const hasAuth = autorizaciones.some((a) => a.estatus === "Vigente");

  const consultar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sic/consultar", { cliente_id: cliente.id, sociedad, producto });
      setResult(data.consulta);
      toast.success(`Consulta a ${sociedad} completada · Score ${data.consulta.score} (${data.consulta.modo})`);
      onChange && onChange();
    } catch (e) {
      toast.error(apiError(e), { duration: 8000 });
    } finally { setBusy(false); }
  };

  const descargar = async (docId) => {
    if (!docId) { toast.error("Este reporte no tiene archivo asociado"); return; }
    try {
      const { data } = await api.post(`/documentos/${docId}/signed-url`);
      window.open(`${BACKEND}${data.url}`, "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };

  const rep = result?.reporte;

  return (
    <div className="space-y-4" data-testid="sic-consulta">
      {/* Score persistido + acción de consulta */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Gauge className="h-5 w-5 text-primary" />
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Score más reciente</p>
              <p className={`font-heading text-2xl font-bold ${scoreColor(cliente.sic_score)}`} data-testid="sic-score-actual">
                {cliente.sic_score || "—"}
              </p>
              <p className="text-[11px] text-slate-400">
                {cliente.sic_score
                  ? `${cliente.sic_score_sociedad || ""} · ${fdate(cliente.sic_score_fecha) || ""}`
                  : "Sin consultas registradas"}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <p className="mb-1 text-[10px] uppercase text-slate-400">Sociedad</p>
              <Select value={sociedad} onValueChange={setSociedad}>
                <SelectTrigger className="h-9 w-44" data-testid="sic-sociedad"><SelectValue /></SelectTrigger>
                <SelectContent>{SOCIEDADES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <p className="mb-1 text-[10px] uppercase text-slate-400">Producto</p>
              <Select value={producto} onValueChange={setProducto}>
                <SelectTrigger className="h-9 w-40" data-testid="sic-producto"><SelectValue /></SelectTrigger>
                <SelectContent>{PRODUCTOS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {hasAuth && (
              <Button onClick={consultar} disabled={busy} className="bg-accent hover:bg-forest-700" data-testid="sic-consultar-btn">
                {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileSearch className="mr-1.5 h-4 w-4" />}
                Consultar {sociedad.split(" ")[0]}
              </Button>
            )}
          </div>
        </div>
        {!hasAuth && (
          <p className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-700" data-testid="sic-sin-auth">
            <AlertTriangle className="h-3.5 w-3.5" /> Sin autorización expresa vigente del cliente (LRSIC). Registra la decisión del cliente abajo (firma de carta o negativa).
          </p>
        )}
      </div>

      {!hasAuth && (
        <SicAutorizacionProspecto idField="cliente_id" recordId={cliente.id} sujeto="cliente"
          showConsultar={false} onAuth={onChange} />
      )}

      {/* Resultado de la consulta (reporte completo) */}
      {rep && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="sic-resultado">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-heading text-sm font-semibold text-emerald-700">
                Reporte {rep.sociedad} · folio {result.folio} <span className="text-xs font-normal text-slate-400">({result.modo})</span>
              </p>
              <p className="text-xs text-slate-500">
                Score <b className={scoreColor(rep.score)}>{rep.score}</b> · {rep.resultado} · Riesgo {rep.riesgo_sugerido}
                {rep.no_hit && " · Sin historial (no hit)"}
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => descargar(result.documento_id)} data-testid="sic-descargar-btn">
              <Download className="mr-1.5 h-4 w-4" /> Descargar reporte
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[["Créditos abiertos", rep.creditos_abiertos], ["Vencidos", rep.creditos_vencidos],
              ["Peor atraso (días)", rep.peor_atraso_dias], ["Consultas recientes", rep.consultas_recientes]].map(([l, v]) => (
              <div key={l} className="rounded-md border border-slate-200 bg-white p-2 text-center">
                <p className="text-[10px] uppercase text-slate-400">{l}</p>
                <p className="text-sm font-semibold text-slate-800">{v ?? 0}</p>
              </div>
            ))}
          </div>
          {rep.cuentas?.length > 0 && (
            <div className="mt-2 overflow-x-auto rounded-lg border border-slate-200 bg-white">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-2 py-2">Otorgante</th><th className="px-2 py-2">Tipo</th>
                    <th className="px-2 py-2 text-right">Saldo</th><th className="px-2 py-2 text-right">Límite</th>
                    <th className="px-2 py-2">MOP</th><th className="px-2 py-2 text-right">Atraso</th>
                    <th className="px-2 py-2">Estatus</th>
                  </tr>
                </thead>
                <tbody>
                  {rep.cuentas.map((cu, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`sic-cuenta-${i}`}>
                      <td className="px-2 py-1.5 font-medium text-slate-700">{cu.otorgante}</td>
                      <td className="px-2 py-1.5">{cu.tipo}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{mxn(cu.saldo)}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{mxn(cu.limite)}</td>
                      <td className="px-2 py-1.5">{cu.mop}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{cu.atraso_dias || 0}</td>
                      <td className="px-2 py-1.5">{cu.estatus}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Historial */}
      <div>
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Autorizaciones del cliente</p>
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <tr><th className="px-2 py-2">Sociedad</th><th className="px-2 py-2">Estatus</th><th className="px-2 py-2">Fecha</th><th className="px-2 py-2">Usuario</th></tr>
            </thead>
            <tbody>
              {(autorizaciones || []).length === 0 && <tr><td colSpan={4} className="px-2 py-3 text-center text-slate-400">Sin autorizaciones</td></tr>}
              {autorizaciones.map((a) => (
                <tr key={a.id} className="border-t border-slate-100">
                  <td className="px-2 py-1.5">{a.sociedad}</td>
                  <td className="px-2 py-1.5"><StatusBadge value={a.estatus} /></td>
                  <td className="px-2 py-1.5">{fdate(a.fecha)}</td>
                  <td className="px-2 py-1.5">{a.usuario}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Consultas a Círculo/Buró</p>
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <tr><th className="px-2 py-2">Sociedad</th><th className="px-2 py-2 text-right">Score</th><th className="px-2 py-2">Resultado</th><th className="px-2 py-2">Folio</th><th className="px-2 py-2">Origen</th><th className="px-2 py-2 text-right">Fecha</th><th className="px-2 py-2"></th></tr>
            </thead>
            <tbody>
              {(consultas || []).length === 0 && <tr><td colSpan={7} className="px-2 py-3 text-center text-slate-400">Sin consultas</td></tr>}
              {consultas.map((q) => (
                <tr key={q.id} className="border-t border-slate-100" data-testid={`sic-consulta-row-${q.id}`}>
                  <td className="px-2 py-1.5">{q.sociedad}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{q.score ?? "—"}</td>
                  <td className="px-2 py-1.5">{q.resultado ?? "—"}</td>
                  <td className="px-2 py-1.5 font-mono text-[10px]">{q.folio}</td>
                  <td className="px-2 py-1.5"><OrigenBadge modo={q.modo} /></td>
                  <td className="px-2 py-1.5 text-right font-mono text-[11px]">{fdate(q.fecha) || "—"}</td>
                  <td className="px-2 py-1.5 text-right">
                    {q.documento_id && (
                      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => descargar(q.documento_id)} data-testid={`sic-descargar-${q.id}`}>
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Fase 8+ (feb-2026) — Distintivo visual del origen de la consulta SIC.
// Regla del usuario: verde para API real (Sandbox/Producción), azul para Manual
// (portal web del buró, cargado por el ejecutivo), gris para "Simulación" legacy
// (datos históricos previos al bloqueo estricto de conexión real). Sin `modo`
// visible por default: se rotula "—".
function OrigenBadge({ modo }) {
  const m = String(modo || "").trim();
  const cls =
    m === "Manual" ? "bg-sky-100 text-sky-800 border-sky-200" :
    (m === "Produccion" || m === "Sandbox") ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
    m === "Simulación" ? "bg-slate-100 text-slate-500 border-slate-200" :
    "bg-slate-50 text-slate-400 border-slate-200";
  const label =
    m === "Manual" ? "Portal (manual)" :
    m === "Produccion" ? "API · Producción" :
    m === "Sandbox" ? "API · Sandbox" :
    m === "Simulación" ? "Simulación (legacy)" :
    (m || "—");
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${cls}`}
          data-testid={`sic-origen-badge-${m || "sin"}`}>
      {label}
    </span>
  );
}
